import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/Button";
import { Icon } from "@/components/Icon";
import { Select } from "@/components/Select";
import { NavigationLinks } from "@/components/workspace/NavigationLinks";
import type {
    NavigationArea,
    NavigationItem,
} from "@/components/workspace/navigation";

interface WorkspaceSidebarProps {
    areas: NavigationArea[];
    area?: NavigationArea;
    favorites: string[];
    recent: NavigationItem[];
    collapsed?: boolean;
    onArea: (id: string) => void;
    onFavorite: (id: string) => void;
    onNavigate?: () => void;
    onReorderFavorite: (sourceId: string, targetId: string) => void;
}

export const WorkspaceSidebar = ({
    areas,
    area,
    favorites,
    recent,
    collapsed,
    onArea,
    onFavorite,
    onNavigate,
    onReorderFavorite,
}: WorkspaceSidebarProps) => {
    const { t } = useTranslation("global");
    const [draggedFavoriteId, setDraggedFavoriteId] = useState<string | null>(
        null
    );
    const allItems = areas.flatMap((entry) => entry.items);
    const favoriteItems = favorites
        .map((id) => allItems.find((item) => item.id === id))
        .filter((item): item is NavigationItem => Boolean(item));
    const links = (items: NavigationItem[]): React.ReactNode => (
        <NavigationLinks
            items={items}
            favorites={favorites}
            collapsed={collapsed}
            onFavorite={onFavorite}
            onNavigate={onNavigate}
        />
    );
    return (
        <div className="flex h-full flex-col">
            <div className="p-3">
                {!collapsed ? (
                    <Select<string>
                        label={t("workspace.area")}
                        value={area?.id || ""}
                        options={areas.map((entry) => ({
                            value: entry.id,
                            label: entry.label,
                            icon: <Icon value={entry.icon} size={16} />,
                        }))}
                        onChange={onArea}
                        className="w-full"
                        size="sm"
                    />
                ) : (
                    <div className="space-y-1">
                        {areas.map((entry) => (
                            <Button
                                key={entry.id}
                                variant="ghost"
                                icon={entry.icon}
                                onlyicon
                                title={entry.label}
                                aria-label={entry.label}
                                aria-pressed={area?.id === entry.id}
                                onClick={() => onArea(entry.id)}
                                className={`!w-full ${area?.id === entry.id ? "!bg-primary/10 !text-primary" : ""}`}
                            />
                        ))}
                    </div>
                )}
            </div>
            <nav
                aria-label={t("workspace.navigation")}
                className="flex-1 space-y-6 overflow-y-auto px-3 pb-4"
            >
                {links([
                    {
                        id: "home",
                        path: "/",
                        label: t("workspace.home"),
                        icon: "house",
                        exact: true,
                    },
                ])}
                <section>
                    {!collapsed && (
                        <p className="mb-2 px-3 text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-zinc-500">
                            {area?.label}
                        </p>
                    )}
                    {links(area?.items || [])}
                </section>
                {!collapsed && (
                    <section>
                        <p className="mb-2 flex items-center gap-2 px-3 text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-zinc-500">
                            <Icon value="heart" size={13} />
                            {t("menu.favorites.header")}
                        </p>
                        {favoriteItems.length ? (
                            favoriteItems.map((item) => (
                                <div
                                    key={item.id}
                                    draggable
                                    onDragStart={() =>
                                        setDraggedFavoriteId(item.id)
                                    }
                                    onDragEnd={() => setDraggedFavoriteId(null)}
                                    onDragOver={(event) =>
                                        event.preventDefault()
                                    }
                                    onDrop={() => {
                                        if (draggedFavoriteId) {
                                            onReorderFavorite(
                                                draggedFavoriteId,
                                                item.id
                                            );
                                        }
                                        setDraggedFavoriteId(null);
                                    }}
                                    className={`group/favorite ${draggedFavoriteId === item.id ? "opacity-40" : ""}`}
                                >
                                    {links([item])}
                                </div>
                            ))
                        ) : (
                            <p className="px-3 text-xs leading-relaxed text-slate-500 dark:text-zinc-400">
                                {t("workspace.favorites_hint")}
                            </p>
                        )}
                    </section>
                )}
                {!collapsed && recent.length > 0 && (
                    <section>
                        <p className="mb-2 flex items-center gap-2 px-3 text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-zinc-500">
                            <Icon value="clock" size={13} />
                            {t("workspace.recent")}
                        </p>
                        {links(recent)}
                    </section>
                )}
            </nav>
            <div className="border-t border-slate-200 p-3 dark:border-white/10">
                {links([
                    {
                        id: "account",
                        path: "/settings/account",
                        matchPath: "/settings",
                        label: t("settings.title"),
                        icon: "settings",
                    },
                ])}
            </div>
        </div>
    );
};
